/** Tab 10: Safety / Medical */
import { useState } from 'react'
import {
  Button, Card, Form, Input, message, Space, DatePicker, Select, Switch, Row, Col,
} from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { joiningApi, type JoiningDetail } from '@/features/hr/joiningApi'
import { PPE_SIZES, SHOE_SIZES, UNIFORM_SIZES } from '@/shared/types/hr'

interface Props { detail: JoiningDetail }

const PPE_OPTIONS = PPE_SIZES.map((s) => ({ label: s, value: s }))
const SHOE_OPTIONS = SHOE_SIZES.map((s) => ({ label: s, value: s }))
const UNIFORM_OPTIONS = UNIFORM_SIZES.map((s) => ({ label: s, value: s }))

export default function SafetyTab({ detail }: Props) {
  const [form] = Form.useForm()
  const qc = useQueryClient()
  const [dirty, setDirty] = useState(false)

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => joiningApi.update(detail.id, values),
    onSuccess: () => {
      message.success('Saved')
      setDirty(false)
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  const initial: Record<string, unknown> = { ...detail.employee }
  const dateFields = [
    'medical_fitness_date', 'medical_fitness_expiry', 'safety_induction_date',
  ]
  dateFields.forEach((f) => {
    if (initial[f]) initial[f] = dayjs(initial[f] as string)
  })

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initial}
      onValuesChange={() => setDirty(true)}
      onFinish={(values) => {
        const out: Record<string, unknown> = {}
        Object.keys(values).forEach((k) => {
          const v = values[k]
          out[k] = dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : v
        })
        save.mutate(out)
      }}
    >
      <Card size="small" title="Medical Fitness" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item name="medical_fitness_required" label="Required" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="medical_fitness_status" label="Status">
              <Select
                options={[
                  { value: 'PENDING', label: 'Pending' },
                  { value: 'FIT', label: 'Fit' },
                  { value: 'UNFIT', label: 'Unfit' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="medical_fitness_date" label="Test Date">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="medical_fitness_expiry" label="Expiry">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Card size="small" title="Safety Induction" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item name="safety_induction_done" label="Safety Induction Done" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="safety_induction_date" label="Induction Date">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="fire_safety_done" label="Fire Safety Done" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="machine_safety_done" label="Machine Safety Done" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Card size="small" title="PPE / Sizes" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="ppe_size" label="PPE Size">
              <Select allowClear placeholder="XS / S / M / L / XL / ..." options={PPE_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="shoe_size"
              label="Shoe Size"
              tooltip="UK shoe size — same scale used on safety shoes sold in India."
            >
              <Select allowClear placeholder="UK size 5–13" options={SHOE_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="uniform_size" label="Uniform Size">
              <Select allowClear placeholder="XS / S / M / L / XL / ..." options={UNIFORM_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="work_restriction" label="Work Restriction">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Space>
        <Button type="primary" htmlType="submit" disabled={!dirty} loading={save.isPending}>
          Save
        </Button>
        {dirty && <span style={{ color: '#faad14' }}>Unsaved changes</span>}
      </Space>
    </Form>
  )
}
